/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef } from 'react';
import { useTrackingConsent } from '@/hooks/use-tracking-consent';
import { TrackingConsent } from '@/types/tracking-consent';
import { CONSENT_WAIT_FOR_UPDATE_MS, consentState } from './consent';
import { GTM_ORIGIN } from './container-id';

declare global {
    interface Window {
        dataLayer?: unknown[];
        // Defined by the inline snippet in <GoogleTagManagerScript>. Untyped varargs by
        // design — this is Google's own gtag signature.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        gtag?: (...args: any[]) => void;
    }
}

/**
 * Google Tag Manager container snippet. Render inside `<head>`, as high as possible.
 *
 * Three things happen here, in order, and the order matters:
 *
 * 1. `dataLayer` and the `gtag` shim are created, so nothing that arrives later is dropped.
 * 2. Consent Mode v2 defaults are set for every category the consent banner governs. Defaults
 *    MUST be pushed before `gtm.js` loads — after is too late, tags may already have fired.
 * 3. `gtm.js` is injected asynchronously.
 *
 * Step 2 uses `trackingConsent` when the shopper's choice is already known (see
 * `resolveGtmInitialConsent` in `./consent`) and falls back to **denied** with
 * `wait_for_update` when it is not, so GTM holds tags briefly rather than firing them before
 * the bridge reports a choice.
 *
 * This is why the storefront can serve GTM from SSR `<head>`: either consent is known and
 * asserted, or everything gated stays denied until told otherwise. It is also why
 * `adapters.gtm` needs no `consentCategory` — GTM does its own gating, the case
 * README-CONSENT-TRACKING.md describes as "the adapter manages consent internally".
 *
 * @param containerId - validated container ID from `resolveGtmContainerId`; never raw config
 * @param nonce - per-request CSP nonce for this script element. Not interpolated into the
 *   script body: the snippet reads it back off its own element at runtime instead (see below)
 * @param trackingConsent - the shopper's known choice, from `resolveGtmInitialConsent`
 */
export function GoogleTagManagerScript({
    containerId,
    nonce,
    trackingConsent,
}: {
    containerId: string;
    nonce?: string;
    trackingConsent?: TrackingConsent;
}) {
    const consentDefault =
        trackingConsent === undefined
            ? { ...consentState(false), wait_for_update: CONSENT_WAIT_FOR_UPDATE_MS }
            : consentState(trackingConsent === TrackingConsent.Accepted);

    // `containerId` is interpolated into script source below. It reaches here only via
    // resolveGtmContainerId's strict /^GTM-[A-Z0-9]{4,20}$/ allowlist, so it cannot carry
    // quotes or angle brackets. Do not bypass that resolver.
    //
    // The nonce is deliberately NOT interpolated. `gtm.js` is injected dynamically, so it
    // needs the nonce to satisfy CSP — and GTM propagates that nonce onto many of the vendor
    // tags it injects, which is what keeps script-src free of 'unsafe-inline'. But writing the
    // nonce into the script *body* puts it back in the live DOM as readable text, which is
    // exactly what browsers hide the nonce content attribute to prevent. Reading it off
    // `document.currentScript` instead keeps it in the element's internal slot, where
    // `.nonce` still returns the real value after the attribute has been emptied. This is
    // also the form Google's own CSP guidance uses.
    const snippet = `
window.dataLayer = window.dataLayer || [];
function gtag(){window.dataLayer.push(arguments);}
window.gtag = gtag;
gtag('consent', 'default', ${JSON.stringify(consentDefault)});
(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
var c=d.currentScript,n=c&&(c.nonce||c.getAttribute('nonce'));
var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
j.async=true;j.src='${GTM_ORIGIN}/gtm.js?id='+i+dl;if(n)j.setAttribute('nonce',n);
f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`.trim();

    return (
        <>
            {/* GTM is on every page when enabled, and the snippet injects gtm.js immediately,
                so warming the connection here is worth it (README-PERFORMANCE.md rule 22).
                Rendered with the script rather than via config links.preconnect because the
                origin is only contacted when a container ID actually resolves. */}
            <link rel="preconnect" href={GTM_ORIGIN} />
            <script
                nonce={nonce}
                // Browsers strip the `nonce` content attribute from the DOM after applying CSP,
                // leaving `nonce=""` client-side while the server rendered the real value — a
                // hydration attribute mismatch on hand-written inline scripts. Same treatment as
                // the __APP_CONFIG__ script in root.tsx.
                suppressHydrationWarning
                // Controlled inline script, the same category root.tsx is exempted for. The only
                // interpolated values are `containerId` (constrained by resolveGtmContainerId's
                // /^GTM-[A-Z0-9]{4,20}$/ allowlist) and the consent defaults (a serialized
                // enum-derived object). Neither is user input. GTM's loader has to be inline: it
                // must create dataLayer and set Consent Mode defaults before gtm.js executes.
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: snippet }}
            />
        </>
    );
}

/**
 * `<noscript>` fallback iframe. Render immediately after the opening `<body>` tag.
 *
 * Only reaches shoppers with JavaScript disabled, for whom the snippet above never runs.
 * Needs `frame-src` on the GTM origin (supplied by the GTM CSP contributor).
 */
export function GoogleTagManagerNoScript({ containerId }: { containerId: string }) {
    return (
        <noscript>
            <iframe
                src={`${GTM_ORIGIN}/ns.html?id=${containerId}`}
                height="0"
                width="0"
                style={{ display: 'none', visibility: 'hidden' }}
                title="Google Tag Manager"
            />
        </noscript>
    );
}

/**
 * Keeps GTM's Consent Mode state in step with the shopper's choice *after* first render.
 *
 * Mount inside the provider tree (it reads auth + config through `useTrackingConsent`),
 * alongside `<TrackingConsentBanner />`.
 *
 * The snippet already asserts the choice the server knew about, so this is not what grants a
 * returning shopper — it covers everything that changes without a document load: the banner
 * click, and the reverse direction when a previously reported choice goes away.
 *
 * A decline is pushed explicitly rather than left to the default — it tells GTM the shopper
 * has decided, so it stops waiting instead of stalling until the timeout.
 */
export function GoogleTagManagerConsentBridge() {
    const { trackingConsent, isTrackingConsentEnabled } = useTrackingConsent();
    const lastPushed = useRef<TrackingConsent | null>(null);

    useEffect(() => {
        // Nothing to report when the consent feature is off — GTM keeps its denied defaults
        // rather than being silently granted by a storefront that shows no banner.
        //
        // This guard MUST stay above the undetermined branch below. `useTrackingConsent`
        // forces `trackingConsent` to undefined whenever the feature is disabled, so from
        // here the two states are indistinguishable, and the branch below pushes.
        if (!isTrackingConsentEnabled) return;
        if (typeof window === 'undefined') return;

        const gtag = window.gtag;
        if (typeof gtag !== 'function') return;

        if (trackingConsent === undefined) {
            // Nothing reported yet: the shopper has not chosen. The snippet's denied defaults
            // already cover them, and pushing here would only close the `wait_for_update`
            // window before they have had a chance to decide.
            if (lastPushed.current === null) return;

            // Previously reported, now undetermined — the choice was lost, not never made.
            // Logout clears it from the SLAS token, and a token/cookie mismatch does the same;
            // both arrive via client-side navigation, so the page and its granted GTM state
            // survive. The banner reappears at that same moment, so leaving GTM granted would
            // mean tracking a shopper who is being asked to consent. Walk it back.
            gtag('consent', 'update', consentState(false));
            lastPushed.current = null;
            return;
        }

        if (lastPushed.current === trackingConsent) return;

        // On mount this re-states what the snippet already asserted for a shopper whose choice
        // was known server-side. That is deliberate: an `update` identical to the `default` is
        // a no-op for GTM, and deriving the initial ref from the snippet instead would couple
        // the two and silently skip a correction whenever they disagreed.
        gtag('consent', 'update', consentState(trackingConsent === TrackingConsent.Accepted));
        lastPushed.current = trackingConsent;
    }, [trackingConsent, isTrackingConsentEnabled]);

    return null;
}
